import React, { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { PROFILE_ENDPOINT } from '../config/api';
import './TermsAcceptanceModal.css';

function TermsAcceptanceModal({ onAccept }) {
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = async () => {
    if (!accepted) {
      setError('You must accept the Terms and Conditions to continue');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      // Get current profile first
      const getResponse = await fetch(PROFILE_ENDPOINT, {
        method: 'GET',
        headers: {
          'Authorization': token
        }
      });

      if (!getResponse.ok) {
        throw new Error('Failed to load profile');
      }

      const currentData = await getResponse.json();
      const currentProfile = currentData.statusCode ? JSON.parse(currentData.body) : currentData;

      // Verify profile is complete before updating
      if (!currentProfile.profile_completed || !currentProfile.first_name || !currentProfile.last_name || !currentProfile.gender) {
        throw new Error('Profile must be completed before accepting terms. Please complete your profile first.');
      }

      // Update with terms acceptance - preserve all existing fields
      const requestBody = {
        first_name: currentProfile.first_name,
        last_name: currentProfile.last_name,
        gender: currentProfile.gender,
        terms_accepted: true
      };

      if (currentProfile.gender_other) {
        requestBody.gender_other = currentProfile.gender_other;
      }
      if (currentProfile.age_range) {
        requestBody.age_range = currentProfile.age_range;
      }
      if (currentProfile.primary_role) {
        requestBody.primary_role = currentProfile.primary_role;
      }
      if (currentProfile.living_situation) {
        requestBody.living_situation = currentProfile.living_situation;
      }
      if (currentProfile.relationship_status) {
        requestBody.relationship_status = currentProfile.relationship_status;
      }

      const response = await fetch(PROFILE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update terms acceptance');
      }

      const data = await response.json();
      
      if (data.statusCode && data.statusCode !== 200) {
        const errorBody = JSON.parse(data.body);
        throw new Error(errorBody.error || 'Failed to update terms acceptance');
      }

      onAccept();
    } catch (err) {
      console.error('Terms acceptance error:', err);
      setError(err.message || 'Failed to accept terms. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="terms-modal-overlay">
      <div className="terms-modal">
        <div className="terms-modal-header">
          <h2>Terms and Conditions Updated</h2>
          <p>Please review and accept our Terms and Conditions to continue using 3AM AI</p>
        </div>

        <div className="terms-modal-content">
          <div className="terms-scroll-area">
            <h3>Critical Disclaimers</h3>
            <p><strong>3AM AI is NOT a medical or therapeutic service.</strong> The Service:</p>
            <ul>
              <li>Does NOT provide medical advice, diagnosis, or treatment</li>
              <li>Does NOT replace professional mental health care</li>
              <li>Is NOT a substitute for therapy, counseling, or psychiatric care</li>
              <li>Should NOT be used for medical or mental health emergencies</li>
            </ul>

            <h3>Emergency Situations</h3>
            <p><strong>IF YOU ARE EXPERIENCING A MEDICAL OR MENTAL HEALTH EMERGENCY:</strong></p>
            <ul>
              <li><strong>Call 000</strong> (Emergency Services - Australia)</li>
              <li><strong>Call Lifeline: 13 11 14</strong> (24/7 Crisis Support)</li>
              <li><strong>Call Beyond Blue: 1300 22 4636</strong> (Mental Health Support)</li>
              <li><strong>Go to your nearest hospital emergency department</strong></li>
            </ul>

            <h3>AI Limitations</h3>
            <p>The Service uses artificial intelligence which can:</p>
            <ul>
              <li>Make mistakes or provide inaccurate information</li>
              <li>Misunderstand context or intent</li>
              <li>Generate inappropriate or incorrect responses</li>
              <li>Not detect serious mental health conditions or crises</li>
            </ul>

            <h3>Your Responsibilities</h3>
            <p>By using this service, you agree:</p>
            <ul>
              <li>You are at least 18 years of age</li>
              <li>You will not use the Service for medical or mental health emergencies</li>
              <li>You understand AI can make mistakes</li>
              <li>You will seek professional help for medical or mental health concerns</li>
            </ul>

            <p className="terms-full-link">
              <a href="/terms" target="_blank" rel="noopener noreferrer">View full Terms and Conditions</a>
            </p>
          </div>

          <div className="terms-modal-checkbox">
            <input
              type="checkbox"
              id="terms-accept-checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              disabled={loading}
            />
            <label htmlFor="terms-accept-checkbox">
              I have read and agree to the Terms and Conditions
            </label>
          </div>

          {error && <div className="terms-modal-error">{error}</div>}

          <button
            className="terms-modal-accept-btn"
            onClick={handleAccept}
            disabled={!accepted || loading}
          >
            {loading ? 'Accepting...' : 'Accept and Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TermsAcceptanceModal;
